import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { useDashboardTheme } from "../../../../context/DashboardThemeContext";
import { useAlert } from "../../../../context/AlertContext";

export default function ProductBrands() {
  const { dashboardTheme } = useDashboardTheme();
  const { showSuccess, showError } = useAlert();
  const isMobile = useMediaQuery("(max-width:900px)");
  const rowsPerPage = isMobile ? 10 : 30;

  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [form, setForm] = useState({
    name: "",
    estado: "activo",
  });

  const token = localStorage.getItem("accessToken");

  const buttonSx = {
    ...dashboardTheme.buttonStyles.contained,
    fontWeight: 700,
  };

  const outlinedButtonSx = {
    minHeight: 38,
    px: 2,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 700,
    color: dashboardTheme.button_color,
    borderColor: dashboardTheme.button_color,
    boxShadow: "none",
    "&:hover": {
      borderColor: dashboardTheme.button_color,
      bgcolor: `${dashboardTheme.button_color}12`,
      boxShadow: "none",
    },
  };

  const dangerButtonSx = {
    minHeight: 38,
    px: 2,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 700,
    bgcolor: "#dc2626",
    color: "#ffffff",
    boxShadow: "none",
    "&:hover": {
      bgcolor: "#b91c1c",
      boxShadow: "none",
    },
  };

  const loadItems = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();

      if (search.trim()) {
        params.append("q", search.trim());
      }

      if (estadoFilter) {
        params.append("estado", estadoFilter);
      }

      const response = await fetch(`/api/products/brands/?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        showError(data.error || data.detail || "No se pudieron cargar las marcas");
        return;
      }

      setItems(data.results || []);
    } catch {
      showError("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      setPage(0);
      loadItems();
    }, 350);

    return () => clearTimeout(delay);
  }, [search, estadoFilter]);

  useEffect(() => {
    setPage(0);
  }, [rowsPerPage]);

  const visibleItems = useMemo(() => {
    const start = page * rowsPerPage;
    return items.slice(start, start + rowsPerPage);
  }, [items, page, rowsPerPage]);

  const openCreate = () => {
    setSelectedItem(null);
    setForm({
      name: "",
      estado: "activo",
    });
    setFormOpen(true);
  };

  const openEdit = (item) => {
    setSelectedItem(item);
    setForm({
      name: item.name || "",
      estado: item.estado || "activo",
    });
    setFormOpen(true);
  };

  const openDelete = (item) => {
    setSelectedItem(item);
    setDeleteOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
  };

  const closeDelete = () => {
    if (saving) return;
    setDeleteOpen(false);
  };

  const saveItem = async () => {
    if (!form.name.trim()) {
      showError("El nombre es obligatorio");
      return;
    }

    try {
      setSaving(true);

      const isEdit = !!selectedItem?.id;
      const url = isEdit
        ? `/api/products/brands/${selectedItem.id}/`
        : "/api/products/brands/create/";

      const response = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: form.name.trim(),
          estado: form.estado,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const error = data.error;
        if (typeof error === "object") {
          showError(Object.values(error).flat().join(" "));
        } else {
          showError(error || data.detail || "No se pudo guardar la marca");
        }
        return;
      }

      showSuccess(data.mensaje || "Marca guardada correctamente");
      setFormOpen(false);
      loadItems();
    } catch {
      showError("Error de conexión");
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async () => {
    if (!selectedItem?.id) return;

    try {
      setSaving(true);

      const response = await fetch(`/api/products/brands/${selectedItem.id}/delete/`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        showError(data.error || data.detail || "No se pudo eliminar la marca");
        return;
      }

      showSuccess(data.mensaje || "Marca eliminada correctamente");
      setDeleteOpen(false);
      loadItems();
    } catch {
      showError("Error de conexión");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 19, md: 21 },
            fontWeight: 600,
            color: dashboardTheme.text_color,
          }}
        >
          Marcas
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddRoundedIcon />}
          onClick={openCreate}
          sx={buttonSx}
        >
          Nueva marca
        </Button>
      </Box>

      <Paper elevation={0} sx={dashboardTheme.tableStyles.wrapper}>
        <Box
          sx={{
            ...dashboardTheme.tableStyles.toolbar,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 1.2,
          }}
        >
          <TextField
            fullWidth
            size="small"
            label="Buscar marca"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              maxWidth: { xs: "100%", md: 420 },
              ...dashboardTheme.inputStyles,
            }}
          />

          <FormControl
            fullWidth
            size="small"
            sx={{
              maxWidth: { xs: "100%", md: 210 },
              ...dashboardTheme.inputStyles,
            }}
          >
            <InputLabel>Estado</InputLabel>
            <Select
              label="Estado"
              value={estadoFilter}
              onChange={(e) => setEstadoFilter(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="activo">Activo</MenuItem>
              <MenuItem value="inactivo">Inactivo</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={dashboardTheme.tableStyles.tableArea}>
          <TableContainer sx={dashboardTheme.tableStyles.container}>
            <Table size="small" sx={dashboardTheme.tableStyles.table}>
              <TableHead>
                <TableRow sx={dashboardTheme.tableStyles.headRow}>
                  <TableCell
                    sx={{
                      ...dashboardTheme.tableStyles.headCell,
                      minWidth: 220,
                    }}
                  >
                    Nombre
                  </TableCell>

                  <TableCell sx={dashboardTheme.tableStyles.headCell}>
                    Estado
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={dashboardTheme.tableStyles.headCell}
                  >
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {visibleItems.map((item) => (
                  <TableRow key={item.id} hover sx={dashboardTheme.tableStyles.bodyRow}>
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: dashboardTheme.text_color,
                        }}
                      >
                        {item.name || "-"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {item.estado === "activo" ? "Activo" : "Inactivo"}
                    </TableCell>

                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Stack direction="row" spacing={0.8} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditRoundedIcon />}
                          onClick={() => openEdit(item)}
                          sx={outlinedButtonSx}
                        >
                          Editar
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteOutlineRoundedIcon />}
                          onClick={() => openDelete(item)}
                          sx={{
                            minHeight: 38,
                            px: 2,
                            borderRadius: "10px",
                            textTransform: "none",
                            fontWeight: 700,
                            boxShadow: "none",
                          }}
                        >
                          Eliminar
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

                {!loading && visibleItems.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={dashboardTheme.tableStyles.emptyCell}
                    >
                      No hay marcas registradas
                    </TableCell>
                  </TableRow>
                )}

                {loading && (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={dashboardTheme.tableStyles.emptyCell}
                    >
                      Cargando marcas...
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <TablePagination
          component="div"
          count={items.length}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={dashboardTheme.tableStyles.pagination}
        />
      </Paper>

      <Dialog
        open={formOpen}
        onClose={closeForm}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            bgcolor: dashboardTheme.card_background,
            border: `1px solid ${dashboardTheme.border_color}`,
            boxShadow: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: dashboardTheme.primary_color,
            fontWeight: 600,
            fontSize: 18,
          }}
        >
          {selectedItem ? "Editar marca" : "Nueva marca"}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              fullWidth
              size="small"
              label="Nombre"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              sx={dashboardTheme.inputStyles}
            />

            <FormControl fullWidth size="small" sx={dashboardTheme.inputStyles}>
              <InputLabel>Estado</InputLabel>
              <Select
                label="Estado"
                value={form.estado}
                onChange={(e) => setForm((prev) => ({ ...prev, estado: e.target.value }))}
              >
                <MenuItem value="activo">Activo</MenuItem>
                <MenuItem value="inactivo">Inactivo</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={closeForm} disabled={saving} sx={outlinedButtonSx}>
            Cancelar
          </Button>

          <Button variant="contained" onClick={saveItem} disabled={saving} sx={buttonSx}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={closeDelete}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            bgcolor: dashboardTheme.card_background,
            border: `1px solid ${dashboardTheme.border_color}`,
            boxShadow: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "#dc2626",
            fontWeight: 600,
            fontSize: 18,
          }}
        >
          Eliminar marca
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              color: dashboardTheme.text_color,
              fontSize: 14,
              lineHeight: 1.6,
            }}
          >
            ¿Deseas eliminar la marca {selectedItem?.name || "seleccionada"}?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={closeDelete} disabled={saving} sx={outlinedButtonSx}>
            Cancelar
          </Button>

          <Button variant="contained" onClick={deleteItem} disabled={saving} sx={dangerButtonSx}>
            {saving ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}